/**
 * หน้าค้นหาสำหรับหัวหน้างานและผู้ดูแลระบบ
 *
 * ใช้ cursor pagination ฝั่ง server เท่านั้น ไม่โหลดข้อมูลทั้งหมดลง browser
 * ตัวกรองทุกตัวถูกส่งไปเป็น query string และถูกบังคับ scope ตามสิทธิ์ที่ฝั่ง server อีกชั้น
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError, type ExportJobView, type SessionResponse } from '../lib/api';
import type { CaptureStatus, CaptureView } from '../../shared/types';
import { CAPTURE_STATUSES, CAPTURE_STATUS_LABELS_TH } from '../../shared/types';
import { formatDateTimeTh } from '../../shared/time';
import { metres, statusLabel, statusPillClass } from '../lib/format';

interface Filters {
  q: string;
  status: CaptureStatus | '';
  areaCode: string;
  gpsOverrideOnly: boolean;
}

const EMPTY_FILTERS: Filters = { q: '', status: '', areaCode: '', gpsOverrideOnly: false };

export function SearchRoute({ session }: { session: SessionResponse }) {
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<CaptureView[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** งาน export ที่กำลังติดตามอยู่ null = ยังไม่ได้สั่ง */
  const [exportJob, setExportJob] = useState<ExportJobView | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportStarting, setExportStarting] = useState(false);

  const load = useCallback(
    // ดูเหตุผลเดียวกับใน MyList — setState แบบ synchronous ใน effect ทำให้ render ซ้ำเปล่า ๆ
    async (current: Filters, nextCursor?: string) => {
      try {
        const params = new URLSearchParams({ limit: '25' });
        if (current.q) params.set('q', current.q);
        if (current.status) params.set('status', current.status);
        if (current.areaCode) params.set('areaCode', current.areaCode);
        if (current.gpsOverrideOnly) params.set('gpsOverrideOnly', 'true');
        if (nextCursor) params.set('cursor', nextCursor);
        // นับจำนวนรวมเฉพาะหน้าแรก เพราะ COUNT บนตารางสองแสนแถวมีต้นทุน
        else params.set('withTotal', 'true');

        const page = await api.listCaptures(params.toString());
        setItems((existing) => (nextCursor ? [...existing, ...page.items] : page.items));
        setCursor(page.nextCursor);
        if (!nextCursor) setTotal(page.estimatedTotal ?? null);
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'ค้นหาไม่สำเร็จ');
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    /*
     * eslint-disable-next-line react-hooks/set-state-in-effect --
     * นี่คือการโหลดข้อมูลตอน mount ซึ่งจำเป็นต้องอยู่ใน effect
     * setState ทุกจุดใน load() เกิดหลัง await แล้วทั้งหมด ไม่ใช่ synchronous
     * แต่ rule ตรวจแบบ transitive จึงมองไม่เห็นความต่างนี้
     * มี AbortController ยกเลิก request เมื่อ component ถูก unmount แล้ว
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void load(applied);
  }, [applied, load]);

  const startExport = async () => {
    setExportStarting(true);
    setExportError(null);
    setExportJob(null);
    try {
      const { job } = await api.createExport({
        filter: {
          ...(applied.q ? { q: applied.q } : {}),
          ...(applied.status ? { status: [applied.status] } : {}),
          ...(applied.areaCode ? { areaCode: applied.areaCode } : {}),
          ...(applied.gpsOverrideOnly ? { gpsOverrideOnly: true } : {}),
        },
      });
      setExportJob(job);
    } catch (caught) {
      setExportError(caught instanceof ApiError ? caught.message : 'สร้างงาน export ไม่สำเร็จ');
    } finally {
      setExportStarting(false);
    }
  };

  /*
   * ตามสถานะงาน export จนกว่าจะเสร็จ
   *
   * ต้อง poll เพราะ Worker ตอบ 202 กลับมาทันทีแล้วไปทำต่อเบื้องหลัง
   * งานเล็กเสร็จในไม่กี่วินาที งานสองแสนแถวใช้เวลาหลายรอบ
   *
   * เดิมหน้านี้ไม่ poll เลย แค่ขึ้นข้อความว่า "ตรวจสถานะได้จากลิงก์ดาวน์โหลดในอีกสักครู่"
   * ทั้งที่ไม่มีลิงก์นั้นอยู่จริงที่ไหนเลย ผู้ใช้จึงรอไปก็ไม่มีอะไรมา
   */
  useEffect(() => {
    if (!exportJob) return;
    if (exportJob.status === 'ready' || exportJob.status === 'failed') return;

    const controller = new AbortController();
    let timer: ReturnType<typeof setTimeout>;
    let attempt = 0;

    const poll = async () => {
      try {
        const { job } = await api.getExport(exportJob.id, controller.signal);
        if (controller.signal.aborted) return;
        setExportJob(job);
        if (job.status === 'ready' || job.status === 'failed') return;

        // ถี่ตอนแรกเพราะงานส่วนใหญ่เล็กและเสร็จไว แล้วค่อยห่างขึ้นสำหรับงานใหญ่
        attempt += 1;
        timer = setTimeout(() => void poll(), Math.min(1000 * 2 ** Math.min(attempt, 4), 15_000));
      } catch (caught) {
        if (controller.signal.aborted) return;
        setExportError(caught instanceof ApiError ? caught.message : 'ตรวจสถานะไฟล์ไม่สำเร็จ');
      }
    };

    timer = setTimeout(() => void poll(), 1000);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [exportJob]);

  return (
    <div className="wrap-wide stack-lg">
      <h1>ค้นหารายการ</h1>

      <form
        className="card stack"
        onSubmit={(event) => {
          event.preventDefault();
          setLoading(true);
          setError(null);
          setApplied(filters);
        }}
      >
        <div className="field">
          <label className="label" htmlFor="q">
            เลขทะเบียนลูกค้า (ขึ้นต้นด้วย)
          </label>
          <input
            id="q"
            className="input mono"
            value={filters.q}
            autoComplete="off"
            onChange={(event) => setFilters({ ...filters, q: event.target.value })}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="status">
            สถานะ
          </label>
          <select
            id="status"
            className="input"
            value={filters.status}
            onChange={(event) =>
              setFilters({ ...filters, status: event.target.value as CaptureStatus | '' })
            }
          >
            <option value="">ทั้งหมด</option>
            {CAPTURE_STATUSES.map((status) => (
              <option key={status} value={status}>
                {CAPTURE_STATUS_LABELS_TH[status]}
              </option>
            ))}
          </select>
        </div>

        {session.user.role === 'administrator' && (
          <div className="field">
            <label className="label" htmlFor="area">
              รหัสพื้นที่
            </label>
            <input
              id="area"
              className="input mono"
              value={filters.areaCode}
              autoComplete="off"
              onChange={(event) =>
                setFilters({ ...filters, areaCode: event.target.value.toUpperCase() })
              }
            />
          </div>
        )}

        <label className="row">
          <input
            type="checkbox"
            checked={filters.gpsOverrideOnly}
            onChange={(event) => setFilters({ ...filters, gpsOverrideOnly: event.target.checked })}
          />
          <span>เฉพาะรายการที่มีการขออนุมัติพิกัด</span>
        </label>

        <div className="row">
          <button type="submit" className="btn grow">
            ค้นหา
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setFilters(EMPTY_FILTERS);
              setLoading(true);
              setApplied(EMPTY_FILTERS);
            }}
          >
            ล้าง
          </button>
        </div>
      </form>

      <div className="spread">
        <span className="small muted">
          {total !== null ? `พบ ${total.toLocaleString('th-TH')} รายการ` : 'ผลการค้นหา'}
        </span>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={exportStarting}
          onClick={() => void startExport()}
        >
          {exportStarting ? 'กำลังสร้าง…' : 'ส่งออก CSV'}
        </button>
      </div>

      {exportError && (
        <div className="banner banner-bad" role="alert">
          <span>{exportError}</span>
        </div>
      )}

      {exportJob && <ExportStatus job={exportJob} onDismiss={() => setExportJob(null)} />}

      {error && (
        <div className="banner banner-bad" role="alert">
          <span>{error}</span>
        </div>
      )}

      {items.length === 0 && !loading && !error && (
        <div className="empty">ไม่พบรายการที่ตรงกับเงื่อนไข</div>
      )}

      {items.length > 0 && (
        <div className="list">
          {items.map((capture) => (
            <Link key={capture.id} to={`/captures/${capture.id}`} className="list-row">
              <div className="grow">
                <b>{capture.customerRegistrationNormalized}</b>
                <p>
                  {formatDateTimeTh(capture.capturedAt)} · ±{metres(capture.accuracyM)} ·{' '}
                  {capture.createdByDisplayName ?? '—'}
                  {capture.areaCode ? ` · ${capture.areaCode}` : ''}
                </p>
              </div>
              {capture.gpsOverride === 'requested' && (
                <span className="pill pill-warn">รออนุมัติ</span>
              )}
              <span className={statusPillClass(capture.status)}>{statusLabel(capture.status)}</span>
            </Link>
          ))}
        </div>
      )}

      {loading && (
        <div className="empty">
          <span className="spinner" style={{ margin: '0 auto' }} />
        </div>
      )}

      {cursor && !loading && (
        <button
          type="button"
          className="btn btn-secondary btn-block"
          onClick={() => {
            setLoading(true);
            void load(applied, cursor);
          }}
        >
          โหลดเพิ่ม
        </button>
      )}
    </div>
  );
}

/**
 * สถานะงาน export และปุ่มดาวน์โหลด
 *
 * แยกออกมาเป็น component เพราะมีสี่สถานะที่ต้องบอกผู้ใช้ให้ต่างกันจริง ๆ
 * ไม่ใช่แค่ข้อความเดียวเปลี่ยนไปมา — โดยเฉพาะกรณีที่ทำเสร็จแล้วซึ่งต้องมีปุ่มให้กด
 */
function ExportStatus({ job, onDismiss }: { job: ExportJobView; onDismiss: () => void }) {
  if (job.status === 'failed') {
    return (
      <div className="banner banner-bad" role="alert">
        <div className="grow">
          <b>สร้างไฟล์ไม่สำเร็จ</b>
          สาเหตุ: {job.errorCode ?? 'ไม่ทราบ'} — ลองใหม่อีกครั้ง ถ้ายังไม่ได้ให้แจ้งผู้ดูแลระบบ
        </div>
        <button type="button" className="btn btn-quiet" onClick={onDismiss}>
          ปิด
        </button>
      </div>
    );
  }

  if (job.status === 'expired') {
    return (
      <div className="banner banner-warn">
        <div className="grow">ไฟล์หมดอายุแล้ว กรุณากดส่งออกใหม่</div>
        <button type="button" className="btn btn-quiet" onClick={onDismiss}>
          ปิด
        </button>
      </div>
    );
  }

  if (job.status === 'ready') {
    const sizeMb = job.sizeBytes ? (job.sizeBytes / (1024 * 1024)).toFixed(1) : null;

    /*
     * ไฟล์ที่ถูกตัดต้องบอกให้ชัด ไม่ใช่ปล่อยให้ดาวน์โหลดไปเงียบ ๆ
     * เพราะคนเอาไปทำรายงานต่อจะไม่มีทางรู้ว่าข้อมูลขาด
     */
    if (job.truncated) {
      return (
        <div className="banner banner-warn">
          <div className="grow">
            <b>ไฟล์พร้อมแล้ว แต่ได้ข้อมูลไม่ครบ</b>
            ไฟล์มี {job.rowsWritten.toLocaleString('th-TH')} แถว ซึ่งเป็นเพดานสูงสุดต่อหนึ่งไฟล์
            ข้อมูลที่ตรงตัวกรองอาจมีมากกว่านี้ — กรุณาแบ่งช่วงวันที่หรือพื้นที่แล้วส่งออกทีละส่วน
          </div>
          <a className="btn" href={api.exportDownloadUrl(job.id)} download>
            ดาวน์โหลดเท่าที่มี
          </a>
        </div>
      );
    }

    return (
      <div className="banner banner-ok">
        <div className="grow">
          <b>ไฟล์พร้อมดาวน์โหลดแล้ว</b>
          {job.rowsWritten.toLocaleString('th-TH')} แถว
          {sizeMb ? ` · ${sizeMb} MB` : ''}
          {job.expiresAt ? ` · ลิงก์ใช้ได้ถึง ${formatDateTimeTh(job.expiresAt)}` : ''}
        </div>
        {/*
          ลิงก์ตรงไม่ใช่ปุ่มที่ fetch เอง เพราะไฟล์อาจใหญ่หลายสิบเมกะไบต์
          ปล่อยให้เบราว์เซอร์จัดการดาวน์โหลดเองประหยัดหน่วยความจำกว่า
          และได้แถบความคืบหน้าของเบราว์เซอร์มาฟรี
        */}
        <a className="btn" href={api.exportDownloadUrl(job.id)} download>
          ดาวน์โหลด CSV
        </a>
      </div>
    );
  }

  // queued หรือ running
  return (
    <div className="banner banner-info" role="status">
      <span className="spinner" />
      <div className="grow">
        <b>กำลังสร้างไฟล์…</b>
        {job.rowsWritten > 0
          ? `เขียนไปแล้ว ${job.rowsWritten.toLocaleString('th-TH')} แถว`
          : 'เริ่มประมวลผลแล้ว'}
        <div className="small muted" style={{ marginTop: 4 }}>
          ไฟล์ขนาดใหญ่ใช้เวลาสักครู่ เปิดหน้านี้ค้างไว้ได้ ระบบจะแจ้งเมื่อพร้อม
        </div>
      </div>
    </div>
  );
}
